import cx from 'classnames'
import { FC, useMemo } from 'react'
import { motionGroup, motionSlide, slideReveal } from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { MotionMask, MotionWrapper } from 'src/theme/styled'

import { ActionButtonProps } from '../ActionButton'
import { BlockTitleProps } from '../BlockTitle'
import { PictureProps } from '../Picture'
import { RichTextProps } from '../RichText'

import * as SC from './styled'

export type TrioPushCardProps = MinimalSeoBlock & {
  title?: string
  richTextProps?: RichTextProps
  actionButtonProps?: ActionButtonProps
}

export type TrioPushProps = {
  className?: string
  blockTitleProps?: BlockTitleProps
  pictureProps?: PictureProps
  color?: string
  pushes: TrioPushCardProps[]
}

const TrioPush: FC<TrioPushProps> = ({
  className,
  blockTitleProps,
  pictureProps,
  color = colors.yellowPale,
  pushes,
}) => {
  const { motion, variants } = useTrioPushMotion()

  return (
    <SC.Container className={cx('TrioPush', className)}>
      {blockTitleProps && <SC._BlockTitle {...blockTitleProps} color={color} />}
      <SC.Wrapper>
        {pictureProps && pictureProps.imageProps && (
          <SC._Picture {...pictureProps} {...motion.picture} />
        )}
        {pushes && pushes.length > 0 && (
          <SC.SliderWrapper {...motion.slider}>
            <SC._Slider
              slides={pushes.slice(0, 3).map((push, index) => {
                return {
                  node: (
                    <SC.Push key={`push-${index}`}>
                      {push.title && (
                        <MotionMask>
                          <SC.PushTitle
                            as={push.htmlTitleTag}
                            variants={variants.title(index)}
                          >
                            {push.title}
                          </SC.PushTitle>
                        </MotionMask>
                      )}
                      {push.richTextProps && (
                        <MotionMask>
                          <MotionWrapper variants={variants.text(index)}>
                            <SC.PushText {...push.richTextProps} />
                          </MotionWrapper>
                        </MotionMask>
                      )}
                      {push.actionButtonProps && (
                        <MotionMask>
                          <MotionWrapper variants={variants.action(index)}>
                            <SC.PushAction {...push.actionButtonProps} />
                          </MotionWrapper>
                        </MotionMask>
                      )}
                      <SC.PushSeparator
                        aria-hidden
                        variants={variants.separator(index)}
                      />
                    </SC.Push>
                  ),
                }
              })}
              keenOptions={{
                drag: true,
                slides: { perView: 1.2, spacing: 16 },
                defaultAnimation: {
                  duration: 300,
                },
                breakpoints: {
                  // 750 as in sizes
                  '(min-width: 46.875em)': {
                    slides: { perView: 1.3, spacing: 24 },
                  },
                  // 900 as in sizes
                  '(min-width: 56.25em)': {
                    disabled: true,
                  },
                },
              }}
            />
          </SC.SliderWrapper>
        )}
        <SC.Decoration aria-hidden $color={color} />
      </SC.Wrapper>
    </SC.Container>
  )
}

const useTrioPushMotion = () => {
  const motions = useMemo(
    () => ({
      picture: motionSlide({}).motionProps,
      slider: motionGroup({}).motionProps,
    }),
    []
  )

  const variants = useMemo(
    () => ({
      title: (index: number) => slideReveal({ delay: 0.2 + index * 0.12 }),
      text: (index: number) => slideReveal({ delay: 0.24 + index * 0.12 }),
      action: (index: number) => slideReveal({ delay: 0.28 + index * 0.12 }),
      separator: (index: number) =>
        slideReveal({ delay: index * 0.12, duration: 1.3, y: '-100%' }, 'ease'),
    }),
    []
  )

  return { motion: motions, variants }
}

export default TrioPush
